import { useEffect } from "react";

/**
 * SEO — tiny helmet replacement. Mutates document.title & <head> meta tags on mount.
 * Also emits JSON-LD scripts via id="json-ld-<key>" and cleans them up on unmount.
 */
export function useSeo({
  title,
  description,
  canonical,
  image,
  ogType = "website",
  jsonLd = [],
}) {
  useEffect(() => {
    if (title) document.title = title;

    const upsertMeta = (attr, key, content) => {
      if (!content) return null;
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
      return el;
    };

    const created = [];
    created.push(upsertMeta("name", "description", description));
    created.push(upsertMeta("property", "og:title", title));
    created.push(upsertMeta("property", "og:description", description));
    created.push(upsertMeta("property", "og:type", ogType));
    created.push(upsertMeta("property", "og:image", image));
    created.push(upsertMeta("name", "twitter:card", "summary_large_image"));
    created.push(upsertMeta("name", "twitter:title", title));
    created.push(upsertMeta("name", "twitter:description", description));
    created.push(upsertMeta("name", "twitter:image", image));

    let linkCanonical = document.head.querySelector('link[rel="canonical"]');
    if (canonical) {
      if (!linkCanonical) {
        linkCanonical = document.createElement("link");
        linkCanonical.setAttribute("rel", "canonical");
        document.head.appendChild(linkCanonical);
      }
      linkCanonical.setAttribute("href", canonical);
    }

    // JSON-LD blocks
    const ldEls = [];
    (jsonLd || []).forEach((block, i) => {
      const id = `json-ld-${i}`;
      let el = document.getElementById(id);
      if (!el) {
        el = document.createElement("script");
        el.type = "application/ld+json";
        el.id = id;
        document.head.appendChild(el);
      }
      el.textContent = JSON.stringify(block);
      ldEls.push(el);
    });

    return () => {
      // don't remove meta on unmount (SPA re-navigation will overwrite);
      // but do clean the JSON-LD blocks so per-page schemas don't stack
      ldEls.forEach((el) => el.remove());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, canonical, image, ogType, JSON.stringify(jsonLd)]);
}
